import { useMemo, useState } from "react";
import { Card, Radio } from "antd";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import Footer from "../components/Footer";
import Header from "../components/Header";
import StandardButton from "../components/StandardButton";

const MODE_ENCODE = "encode";
const MODE_DECODE = "decode";
const VARIANT_STANDARD = "standard";
const VARIANT_HEX = "hex";

const ALPHABETS = {
  [VARIANT_STANDARD]: "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567",
  [VARIANT_HEX]: "0123456789ABCDEFGHIJKLMNOPQRSTUV",
};

const DEFAULT_INPUT = "Quick Tools for Day-to-Day Developer Use";

const encodeText = (text, variant, pad) => {
  const alphabet = ALPHABETS[variant];
  const bytes = new TextEncoder().encode(text);
  if (bytes.length === 0) return "";
  let bits = 0;
  let value = 0;
  let out = "";
  for (let i = 0; i < bytes.length; i += 1) {
    value = (value << 8) | bytes[i];
    bits += 8;
    while (bits >= 5) {
      bits -= 5;
      out += alphabet[(value >>> bits) & 31];
    }
  }
  if (bits > 0) {
    out += alphabet[(value << (5 - bits)) & 31];
  }
  if (pad) {
    const remainder = out.length % 8;
    if (remainder !== 0) out += "=".repeat(8 - remainder);
  }
  return out;
};

const decodeText = (input, variant) => {
  const alphabet = ALPHABETS[variant];
  const lookup = {};
  for (let i = 0; i < alphabet.length; i += 1) lookup[alphabet[i]] = i;

  const cleaned = input.replace(/[\s-]+/g, "").toUpperCase().replace(/=+$/g, "");
  if (cleaned === "") return "";

  let bits = 0;
  let value = 0;
  const bytes = [];
  for (let i = 0; i < cleaned.length; i += 1) {
    const ch = cleaned[i];
    if (!(ch in lookup)) throw new Error("invalid_chars");
    value = (value << 5) | lookup[ch];
    bits += 5;
    if (bits >= 8) {
      bits -= 8;
      bytes.push((value >>> bits) & 0xff);
    }
  }
  const leftover = (value << (8 - bits)) & 0xff;
  if (bits > 0 && leftover !== 0) {
    throw new Error("invalid_chars");
  }
  const arr = new Uint8Array(bytes);
  return new TextDecoder("utf-8", { fatal: true }).decode(arr);
};

const Base32Codec = () => {
  const { t } = useTranslation();
  const [mode, setMode] = useState(MODE_ENCODE);
  const [variant, setVariant] = useState(VARIANT_STANDARD);
  const [pad, setPad] = useState(true);
  const [input, setInput] = useState(DEFAULT_INPUT);

  const { output, error } = useMemo(() => {
    if (input === "") return { output: "", error: null };
    try {
      const result =
        mode === MODE_ENCODE
          ? encodeText(input, variant, pad)
          : decodeText(input, variant);
      return { output: result, error: null };
    } catch {
      return {
        output: "",
        error:
          mode === MODE_ENCODE
            ? "base32_codec_encode_error"
            : "base32_codec_decode_error",
      };
    }
  }, [input, mode, variant, pad]);

  const inputByteLength = useMemo(
    () => new TextEncoder().encode(input).length,
    [input]
  );
  const outputByteLength = useMemo(
    () => new TextEncoder().encode(output).length,
    [output]
  );
  const sizeRatio =
    inputByteLength > 0 && outputByteLength > 0
      ? Math.round((outputByteLength / inputByteLength) * 100)
      : null;

  const swap = () => {
    if (!output || error) {
      toast.error(t("base32_codec_nothing_to_swap"));
      return;
    }
    setInput(output);
    setMode(mode === MODE_ENCODE ? MODE_DECODE : MODE_ENCODE);
  };

  const copy = async () => {
    if (!output) {
      toast.error(t("nothing_to_copy"));
      return;
    }
    try {
      await navigator.clipboard.writeText(output);
      toast.success(t("copy_success"));
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const clear = () => setInput("");

  const inputLabelKey =
    mode === MODE_ENCODE
      ? "base32_codec_input_text_label"
      : "base32_codec_input_b32_label";
  const inputPlaceholderKey =
    mode === MODE_ENCODE
      ? "base32_codec_input_text_placeholder"
      : "base32_codec_input_b32_placeholder";
  const outputLabelKey =
    mode === MODE_ENCODE
      ? "base32_codec_output_b32_label"
      : "base32_codec_output_text_label";

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-6">
            <div>
              <h1 className="text-2xl font-bold text-indigo-700">
                {t("base32_codec_title")}
              </h1>
              <p className="text-gray-600 mt-1">{t("base32_codec_intro")}</p>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t("base32_codec_mode_label")}
                </label>
                <Radio.Group
                  value={mode}
                  onChange={(e) => setMode(e.target.value)}
                >
                  <Radio.Button value={MODE_ENCODE}>
                    {t("base32_codec_mode_encode")}
                  </Radio.Button>
                  <Radio.Button value={MODE_DECODE}>
                    {t("base32_codec_mode_decode")}
                  </Radio.Button>
                </Radio.Group>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t("base32_codec_variant_label")}
                </label>
                <Radio.Group
                  value={variant}
                  onChange={(e) => setVariant(e.target.value)}
                >
                  <Radio.Button value={VARIANT_STANDARD}>
                    {t("base32_codec_variant_standard")}
                  </Radio.Button>
                  <Radio.Button value={VARIANT_HEX}>
                    {t("base32_codec_variant_hex")}
                  </Radio.Button>
                </Radio.Group>
              </div>

              {mode === MODE_ENCODE && (
                <label className="flex items-center gap-2 text-sm text-gray-700 mt-5">
                  <input
                    type="checkbox"
                    checked={pad}
                    onChange={(e) => setPad(e.target.checked)}
                    className="h-4 w-4"
                  />
                  {t("base32_codec_pad_label")}
                </label>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                {t(inputLabelKey)}
              </label>
              <textarea
                rows={6}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none font-mono text-sm"
                placeholder={t(inputPlaceholderKey)}
                value={input}
                onChange={(e) => setInput(e.target.value)}
              />
              <div className="flex flex-wrap gap-3 items-center">
                <StandardButton
                  onClick={swap}
                  type="secondary"
                  variant="outlined"
                  size="small"
                >
                  {t("base32_codec_swap")}
                </StandardButton>
                <StandardButton
                  onClick={clear}
                  type="secondary"
                  variant="outlined"
                  size="small"
                >
                  {t("base32_codec_clear")}
                </StandardButton>
                <span className="text-xs text-gray-500">
                  {t("base32_codec_input_byte_count", {
                    count: inputByteLength,
                  })}
                </span>
              </div>
            </div>

            {error && (
              <p className="text-sm text-red-600">{t(error)}</p>
            )}

            {!error && output && (
              <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200">
                <div className="flex items-center justify-between gap-3">
                  <strong className="text-indigo-700">
                    {t(outputLabelKey)}
                  </strong>
                  <StandardButton
                    onClick={copy}
                    type="secondary"
                    variant="outlined"
                    size="small"
                  >
                    {t("copy_button")}
                  </StandardButton>
                </div>
                <p className="break-all mt-2 text-gray-800 font-mono text-sm whitespace-pre-wrap">
                  {output}
                </p>
                <p className="mt-2 text-xs text-indigo-700/80">
                  {t("base32_codec_output_byte_count", {
                    count: outputByteLength,
                  })}
                  {sizeRatio !== null && (
                    <>
                      {" · "}
                      {t("base32_codec_size_ratio", { percent: sizeRatio })}
                    </>
                  )}
                </p>
              </div>
            )}

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("base32_codec_note_rfc")}</p>
              <p>{t("base32_codec_note_totp")}</p>
              <p>{t("base32_codec_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default Base32Codec;
